<template>
  <v-dialog
    :model-value="props.modelValue"
    @update:model-value="toggleDialog"
    max-width="1100"
  >
    <v-card>
      <v-row class="fluid justify-space-between align-top ma-2">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5 font-weight-medium">Run Tests</span>
        </v-card-title>
        <v-btn variant="text" icon color="grey" @click="toggleDialog(false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-row>

      <v-card-text class="pt-2">
        <div class="d-flex flex-column ga-4">
          <!-- Test Output -->
          <div
            v-if="testOutput || isRunningTests || hasRunTests"
            class="test-output-surface pa-4"
            role="region"
            aria-label="Test output"
            ref="outputContainer"
          >
            <div v-if="isRunningTests && !testOutput" class="d-flex align-center justify-center ga-2">
              <v-progress-circular indeterminate size="20" />
              <span>Running tests...</span>
            </div>
            <pre v-else><code>{{ testOutput || 'No test output yet.' }}</code></pre>
          </div>

          <!-- Test Status -->
          <div v-if="testStatus" class="d-flex justify-center">
            <v-chip
              :color="testStatus === 'passed' ? 'green' : testStatus === 'failed' ? 'red' : 'orange'"
              size="large"
              variant="flat"
            >
              <v-icon start>
                {{ testStatus === 'passed' ? 'mdi-check' : testStatus === 'failed' ? 'mdi-close' : 'mdi-alert' }}
              </v-icon>
              {{ testStatus === 'passed' ? 'Tests Passed' : testStatus === 'failed' ? 'Tests Failed' : 'Tests In Progress' }}
            </v-chip>
          </div>

          <!-- Server Validation Status -->
          <div v-if="serverValidation" class="d-flex justify-center">
            <v-chip
              :color="serverValidationStatus === 'success' ? 'green' : 'red'"
              size="large"
              variant="flat"
            >
              <v-icon start>
                {{ serverValidationStatus === 'success' ? 'mdi-server' : 'mdi-server-off' }}
              </v-icon>
              {{ serverValidationStatus === 'success' ? 'Server Validated' : 'Server Validation Failed' }}
            </v-chip>
          </div>

          <!-- Client Validation Status -->
          <div v-if="clientValidation" class="d-flex justify-center">
            <v-chip
              :color="clientValidationStatus === 'success' ? 'green' : 'red'"
              size="large"
              variant="flat"
            >
              <v-icon start>
                {{ clientValidationStatus === 'success' ? 'mdi-monitor' : 'mdi-monitor-off' }}
              </v-icon>
              {{ clientValidationStatus === 'success' ? 'Client Validated' : 'Client Validation Failed' }}
            </v-chip>
          </div>

          <!-- Enhanced test output to show server details -->
          <div v-if="serverValidation && serverValidation.details" class="mt-4">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon start>mdi-server</v-icon>
                  Server Validation Details
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="text-caption">
                    <div><strong>Script:</strong> {{ serverValidation.details.script }}</div>
                    <div><strong>Port:</strong> {{ serverValidation.details.port }}</div>
                    <div><strong>Endpoints Tested:</strong> {{ serverValidation.details.endpoints?.length || 0 }}</div>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

        </div>
      </v-card-text>

      <v-card-actions class="justify-space-between">
        <div class="text-caption text-medium-emphasis">
          Press Esc to close
        </div>
        <div v-if="hasRunTests" class="d-flex ga-3">
          <v-btn
            variant="outlined"
            size="large"
            :loading="isRunningTests"
            :disabled="isRunningTests"
            @click="runTestsHandler"
          >
            <v-icon start>mdi-refresh</v-icon>
            {{ isRunningTests ? 'Running Tests...' : 'Re-run Tests' }}
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            @click="copyOutput"
            :disabled="!testOutput"
          >
            <v-icon start>mdi-content-copy</v-icon>
            Copy Output
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            @click="clearOutput"
            :disabled="!testOutput"
          >
            <v-icon start>mdi-delete-sweep</v-icon>
            Clear Output
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { runTests } from '@/api/runTestsApi'

const props = defineProps({
  modelValue: Boolean,
  allFiles: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'test-results'])

const store = useStore()

const isRunningTests = ref(false)
const testOutput = ref('')
const testStatus = ref(null) // 'passed', 'failed', 'running', null
const hasRunTests = ref(false)
const outputContainer = ref(null)
const serverValidation = ref(null)
const serverValidationStatus = ref(null) // 'success', 'failed', null
const clientValidation = ref(null)
const clientValidationStatus = ref(null) // 'success', 'failed', null

// Auto-run tests when dialog opens
watch(() => props.modelValue, async (isOpen) => {
  if (isOpen && !hasRunTests.value && !isRunningTests.value) {
    // Wait for DOM to update to ensure dialog is fully rendered
    await nextTick()
    runTestsHandler()
  }
})

const toggleDialog = (value) => {
  emit('update:modelValue', value)
}

const scrollToBottom = async () => {
  await nextTick()
  if (outputContainer.value) {
    outputContainer.value.scrollTop = outputContainer.value.scrollHeight
  }
}

const runTestsHandler = async () => {
  isRunningTests.value = true
  testStatus.value = 'running'
  testOutput.value = ''
  hasRunTests.value = true

  try {
    // Convert allFiles to the format expected by the lambda
    const filesForLambda = {}
    for (const file of props.allFiles) {
      if (file.filepath && file.contents) {
        filesForLambda[file.filepath] = file.contents
      }
    }

    // Streaming callback to handle real-time updates
    const onChunk = (result) => {
      let newOutput = ''

      switch (result.type) {
        case 'start':
          newOutput = `🚀 ${result.message}\n\n`
          break
        case 'progress':
          if (result.stage === 'setup') {
            newOutput = `📁 ${result.message}\n`
          } else if (result.stage === 'install') {
            newOutput = `📦 ${result.message}\n`
          } else if (result.stage === 'test') {
            newOutput = `🧪 ${result.message}\n`
          } else if (result.stage === 'server-start') {
            newOutput = `🚀 ${result.message}\n`
          } else if (result.stage === 'server-test') {
            newOutput = `🔍 ${result.message}\n`
          } else if (result.stage === 'server-stop') {
            newOutput = `🛑 ${result.message}\n`
          } else if (result.stage === 'server-validation') {
            newOutput = `🔧 ${result.message}\n`
          } else if (result.stage === 'client-validation') {
            newOutput = `🖥️ ${result.message}\n`
          } else if (result.stage === 'client-start') {
            newOutput = `🚀 ${result.message}\n`
          } else if (result.stage === 'client-test') {
            newOutput = `🔍 ${result.message}\n`
          } else if (result.stage === 'client-stop') {
            newOutput = `🛑 ${result.message}\n`
          }
          break
        case 'result':
          if (result.success) {
            if (result.serverValidation) {
              // Handle server validation results
              serverValidation.value = result.serverValidation
              serverValidationStatus.value = 'success'
              newOutput = `✅ Server validation: ${result.message}\n`

              if (result.serverValidation.endpoints) {
                newOutput += '\n🔗 Endpoint Results:\n'
                result.serverValidation.endpoints.forEach(endpoint => {
                  const status = endpoint.success ? '✅' : '❌'
                  newOutput += `${status} ${endpoint.endpoint} - Status: ${endpoint.status}\n`
                })
              }
            } else if (result.clientValidation) {
              // Handle client validation results
              clientValidation.value = result.clientValidation
              clientValidationStatus.value = 'success'
              newOutput = `✅ Client validation: ${result.message}\n`

              if (result.clientValidation.endpoints) {
                newOutput += '\n🔗 Client Endpoint Results:\n'
                result.clientValidation.endpoints.forEach(endpoint => {
                  const status = endpoint.success ? '✅' : '❌'
                  newOutput += `${status} ${endpoint.endpoint} - Status: ${endpoint.status}\n`
                })
              }
            } else {
              // Handle regular test results
              newOutput = `✅ ${result.message}\n\n`

              // Add test summary if available
              if (result.summary) {
                newOutput += `📊 ${result.summary}\n`
              }

              // Add coverage info if available
              if (result.coverage) {
                newOutput += `📈 Coverage: ${result.coverage}\n`
              }

              // Add timing info if available
              if (result.timing) {
                newOutput += `⏱️ Duration: ${result.timing}\n`
              }

              // Add warnings if any
              if (result.warnings && result.warnings.length > 0) {
                newOutput += `\n⚠️ Warnings:\n${result.warnings.join('\n')}\n`
              }

              newOutput += `\n📋 Full Test Output:\n${result.stdout}\n`
              if (result.stderr) {
                newOutput += `\n📋 Additional Output:\n${result.stderr}\n`
              }

              testStatus.value = 'passed'
            }
          } else {
            if (result.serverValidation) {
              // Handle server validation failures
              serverValidation.value = result.serverValidation
              serverValidationStatus.value = 'failed'
              newOutput = `❌ Server validation failed: ${result.message}\n`
              if (result.error) {
                newOutput += `\n📋 Error Details:\n${result.error}\n`
              }
            } else if (result.clientValidation) {
              // Handle client validation failures
              clientValidation.value = result.clientValidation
              clientValidationStatus.value = 'failed'
              newOutput = `❌ Client validation failed: ${result.message}\n`
              if (result.error) {
                newOutput += `\n📋 Error Details:\n${result.error}\n`
              }
            } else {
              // Handle regular test failures
              newOutput = `❌ ${result.message}\n\n`

              // Add test summary if available
              if (result.summary) {
                newOutput += `📊 ${result.summary}\n`
              }

              // Add exit code if available
              if (result.exitCode) {
                newOutput += `🔢 Exit Code: ${result.exitCode}\n`
              }

              // Add specific failure details if available
              if (result.failures && result.failures.length > 0) {
                newOutput += `\n❌ Key Failures:\n${result.failures.join('\n')}\n`
              }

              // Add warnings if any
              if (result.warnings && result.warnings.length > 0) {
                newOutput += `\n⚠️ Warnings:\n${result.warnings.join('\n')}\n`
              }

              newOutput += `\n📋 Error Output:\n${result.stderr || result.error}\n`
              if (result.stdout) {
                newOutput += `\n📋 Standard Output:\n${result.stdout}\n`
              }

              testStatus.value = 'failed'
            }
          }

          // Emit test results to parent component
          emit('test-results', {
            success: result.success,
            timestamp: new Date().toISOString(),
            summary: result.summary,
            exitCode: result.exitCode
          })
          break
        case 'complete':
          newOutput = `\n🏁 ${result.message}\n`
          break
        case 'warning':
          newOutput = `⚠️ ${result.message}\n`
          break
        case 'error':
          newOutput = `❌ Error: ${result.message}\n`
          if (result.error) {
            newOutput += `Details: ${result.error}\n`
          }
          testStatus.value = 'failed'

          // Emit test results for error case
          emit('test-results', {
            success: false,
            timestamp: new Date().toISOString(),
            error: result.message
          })
          break
        default:
          newOutput = `${result.message || JSON.stringify(result)}\n`
      }

      // Append new output to existing output
      testOutput.value += newOutput

      // Auto-scroll to bottom to show latest output
      scrollToBottom()
    }

    const response = await runTests({ allFiles: filesForLambda }, onChunk)

    // Handle fallback for non-streaming responses
    if (response.success && !response.streaming) {
      testOutput.value = response.output || response.message || 'Tests completed successfully!'
      testStatus.value = response.status === 'passed' ? 'passed' : 'completed'

      // Emit test results for non-streaming success
      emit('test-results', {
        success: response.status === 'passed',
        timestamp: new Date().toISOString(),
        message: response.message
      })
    } else if (!response.success) {
      testOutput.value = response.error?.message || 'Tests failed'
      testStatus.value = 'failed'

      // Emit test results for non-streaming failure
      emit('test-results', {
        success: false,
        timestamp: new Date().toISOString(),
        error: response.error?.message
      })
    }
  } catch (error) {
    testOutput.value += `\n❌ Error running tests: ${error.message}`
    testStatus.value = 'failed'

    // Emit test results for exception
    emit('test-results', {
      success: false,
      timestamp: new Date().toISOString(),
      error: error.message
    })
  } finally {
    isRunningTests.value = false
  }
}

const clearOutput = () => {
  testOutput.value = ''
  testStatus.value = null
  serverValidation.value = null
  serverValidationStatus.value = null
  clientValidation.value = null
  clientValidationStatus.value = null
  // Keep hasRunTests as true so we can show the Run Tests button
}

const copyOutput = async () => {
  if (testOutput.value) {
    try {
      await navigator.clipboard.writeText(testOutput.value)
      store.commit('snackbar/showMessage', {
        content: 'Test output copied!',
        timeout: 3000,
        color: 'blue'
      }, { root: true })
    } catch (err) {
      store.commit('snackbar/showMessage', {
        content: 'Failed to copy test output.',
        timeout: 3000,
        color: 'red'
      }, { root: true })
    }
  }
}

</script>

<style scoped>
.test-output-surface {
  white-space: pre-wrap;
  background: #0b1020;
  color: #e8eaf6;
  border-radius: 12px;
  max-height: 50vh;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
  min-height: 200px;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgb(var(--v-theme-outline));
    background-origin: content-box;
  }
}
</style>
